import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { DateRangePicker } from 'react-dates';
import { unstable_batchedUpdates } from 'react-dom';

import { INewCalDates } from '~/src/dash/components/calendar/CalendarV2/common';
import { ButtonV2, Col, Cols, spacing } from '~/src/dash/publicaUI';

const updateEndDate = (start: moment.Moment, end: moment.Moment, maxDaysRange: number) => {
  const lastViableDate = start.clone().add(maxDaysRange, 'days');
  return end.isAfter(lastViableDate) ? lastViableDate : end;
};

const CalendarInfoPanel = (
  handleApply: (props: INewCalDates) => void,
  handleCancel: () => void,
): React.ReactNode => {
  return (
    <Cols
      style={{ justifyContent: 'flex-end', alignItems: 'center', padding: '0px 24px 16px' }}
      gap={spacing.small}
    >
      <Col>
        <ButtonV2 onClick={handleApply}>Apply</ButtonV2>
      </Col>
      <Col>
        <ButtonV2 onClick={handleCancel} clearBackground>
          Cancel
        </ButtonV2>
      </Col>
    </Cols>
  );
};

interface IDatesChange {
  startDate: moment.Moment | null;
  endDate: moment.Moment | null;
}

interface ICalendarProps {
  anchorDirection?: 'left' | 'right';
  openDirection?: 'up' | 'down';
  calEndDate: moment.Moment;
  calStartDate: moment.Moment;
  endDateOffset?: number; // use this to set an automated range
  handleApplyDatesChange: (props: INewCalDates) => void;
  isCompareCalendar?: boolean;
  isDisabled?: boolean;
  maxDate?: moment.Moment; // use this to set a block beyond a certain date
  minDate?: moment.Moment; // use this to set a block beyond a certain date
  maxDaysRange?: number;
  uniqueID: string;
}

export const CalendarV2 = ({
  anchorDirection = 'left',
  openDirection = 'down',
  calEndDate,
  calStartDate,
  endDateOffset,
  handleApplyDatesChange,
  isCompareCalendar,
  isDisabled,
  maxDate,
  minDate,

  maxDaysRange,
  uniqueID,
}: ICalendarProps) => {
  const [start, setStart] = useState(calStartDate);
  const [end, setEnd] = useState(calEndDate);
  const [focusedInput, setFocusedInput] = useState<any>(null);

  // store a clone of the dates to leave the original intact in case of reset
  useEffect(() => {
    unstable_batchedUpdates(() => {
      setStart(calStartDate);
      setEnd(calEndDate);
    });
  }, [calStartDate, calEndDate]);

  const onDatesChange = ({ startDate, endDate }: IDatesChange) => {
    unstable_batchedUpdates(() => {
      startDate && setStart(startDate);
      endDate && setEnd(endDate);
    });
  };

  const onFocusChange = (input: any) => {
    setFocusedInput(input);
  };

  const handleApply = () => {
    const areDatesInOrder = start < end;
    const trueStartDate = areDatesInOrder ? start : end;
    const trueEndDate = areDatesInOrder ? end : start;

    const updatedEndDate = maxDaysRange
      ? updateEndDate(trueStartDate, trueEndDate, maxDaysRange)
      : trueEndDate;
    handleApplyDatesChange({
      newStartDate: trueStartDate,
      newEndDate: maxDaysRange ? updatedEndDate : end,
    });
    unstable_batchedUpdates(() => {
      setStart(trueStartDate);
      setEnd(updatedEndDate);
      setFocusedInput(null);
    });
  };

  const handleCancel = () => {
    unstable_batchedUpdates(() => {
      setStart(calStartDate);
      setEnd(calEndDate);
      setFocusedInput(null);
    });
  };

  const isOutsideRange = (day: moment.Moment) => {
    const validDateRange =
      maxDaysRange && maxDaysRange > 0
        ? day.isSameOrBefore(start.clone().add(maxDaysRange - 1, 'days'))
        : true;
    const validMaxDate = maxDate ? day <= maxDate : true;
    const validMinDate = minDate ? day >= minDate : true;
    return !validDateRange || !validMaxDate || !validMinDate;
  };

  return (
    <DateRangePicker
      {...(endDateOffset !== undefined && {
        endDateOffset: (day: moment.Moment) => moment(day).add(endDateOffset, 'd'),
      })}
      calendarInfoPosition='bottom'
      endDate={end}
      endDateId={`${uniqueID}_end_date_id`}
      focusedInput={focusedInput}
      hideKeyboardShortcutsPanel
      initialVisibleMonth={
        isCompareCalendar ? () => calStartDate : () => moment().subtract(1, 'month')
      }
      isOutsideRange={isOutsideRange}
      keepOpenOnDateSelect
      onDatesChange={onDatesChange}
      onFocusChange={onFocusChange}
      renderCalendarInfo={() => CalendarInfoPanel(handleApply, handleCancel)}
      small
      startDate={start}
      startDateId={`${uniqueID}_start_date_id`}
      onClose={handleCancel}
      minimumNights={0}
      disabled={isDisabled}
      anchorDirection={anchorDirection}
      openDirection={openDirection}
    />
  );
};
